import { useRef } from "react";

import useMatrixTitle from "../hooks/useMatrixTitle";
import { ScoreCard } from "../types";

import useScrollControls from "./hooks/useScrollControls";
import MatrixPlaceholder from "./MatrixPlaceholder";
import MatrixRow from "./MatrixRow";
import ScrollButton from "./ScrollButton";
import { isValidMatrix } from "./utils";

export interface MatrixProps {
  scoreCard?: ScoreCard;
  compareCard?: ScoreCard;
  type?: "before" | "after";
  showDiff?: boolean;
  collapsed?: boolean;
  minimal?: boolean;
}

const Matrix = ({
  scoreCard,
  compareCard,
  type = "before",
  showDiff,
  collapsed = true,
  minimal = false,
}: MatrixProps) => {
  const hasTitle = useMatrixTitle(scoreCard!);
  const comparisonHasTitle = useMatrixTitle(compareCard!);
  const columnCount = scoreCard?.[0]?.length || 0;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { showLeftScroll, showRightScroll, checkScroll } = useScrollControls(scrollContainerRef);

  if (!scoreCard || !isValidMatrix(scoreCard)) {
    return <MatrixPlaceholder input={scoreCard} isInvalid={!!scoreCard} />;
  }

  if (columnCount === 0) {
    return null;
  }

  const handleScroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth / 2;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div
      className={`group relative w-full ${minimal ? "" : "rounded-lg border p-4"} ${
        collapsed ? "max-w-full" : "w-full"
      }`}
    >
      <div className="flex flex-col">
        {hasTitle && (
          <div className="mb-4 w-full border-b border-gray-200 pb-3 text-center">
            <h3 className="text-md font-semibold text-gray-900">
              {typeof scoreCard[0][0] === "string" ? scoreCard[0][0] : ""}
            </h3>
          </div>
        )}
        <div
          className={`relative ${collapsed ? "scrollbar-hide overflow-x-auto" : ""}`}
          ref={scrollContainerRef}
          onScroll={collapsed ? checkScroll : undefined}
        >
          <div
            className={`grid gap-1 ${
              columnCount === 1 ? "justify-items-center" : ""
            } ${collapsed ? "min-w-0" : "w-full"}`}
            style={{
              gridTemplateColumns:
                columnCount === 1
                  ? "1fr"
                  : `minmax(120px, auto) repeat(${
                      columnCount - (hasTitle ? 2 : 1)
                    }, minmax(80px, 1fr))`,
              minWidth: collapsed && columnCount > 4 ? "100%" : "auto",
            }}
          >
            {(hasTitle
              ? scoreCard.map((elem, i) => (i === 0 ? elem.slice(1) : elem))
              : scoreCard
            ).map((row, rowIndex) => (
              <MatrixRow
                // eslint-disable-next-line react/no-array-index-key
                key={`${type}-row-${rowIndex}`}
                row={row}
                rowIndex={rowIndex}
                type={type}
                compareCard={
                  comparisonHasTitle
                    ? compareCard!.map((elem, i) => (i === 0 ? elem.slice(1) : elem))
                    : compareCard
                }
                showDiff={showDiff}
              />
            ))}
          </div>
        </div>
      </div>
      {collapsed && showLeftScroll && (
        <ScrollButton direction="left" onClick={() => handleScroll("left")} />
      )}
      {collapsed && showRightScroll && (
        <ScrollButton direction="right" onClick={() => handleScroll("right")} />
      )}
    </div>
  );
};

export default Matrix;
